import env from '@beam-australia/react-env';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from 'styled-components';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router';

import { Spinner } from 'src/components/spinner';
import { store } from 'src/state/configure-store';
import { GlobalStyle } from 'src/theme/global';
import { theme } from 'src/theme/theme';
import { NavigationService } from 'src/services/navigation-service';
import { AuthCallbackPage } from 'src/pages/auth-callback';
import { PageLogout } from 'src/pages/logout';
import { PageSessionsExpired } from 'src/pages/auth-callback/error';
import { AuthenticatedRoute } from 'src/components/authenticated-route';
import { StoreRoutes } from 'src/pages/store-routes';
import { i18n } from 'src/i18n';
import { App } from 'src/app';

import {
  PageUnderMaintenance,
  PageError,
  PageInflightOrder,
  PageMultipleTvOffers,
  PageNoBroadband,
  PageNoServiceLocations,
  PageNotAvailable,
  PageNoTvPlatform,
  PageRestrictedPurchase,
  PageWrongRole,
  PageWrongTvPlatform,
} from './pages/error';

import '@lyse-as/formds-react/assets/stylesheets/formds-altibox.css';
import '@altibox/lte-salgskanaler-design-system-component-lib/dist/index.css';

if (env('ENV') !== 'production') {
  /* eslint-disable-next-line no-console */
  console.info(
    `%cStarting with environment config "${env('ENV')}"`,
    'background-color: #90D5ED; color: #1D2B30; padding: .2em .6em',
  );

  if (!window.location.pathname.includes('/modella')) {
    window.history.replaceState('', '', '/modella');
  }
}

document.title = i18n.document.title;
document.documentElement.lang = i18n.document.langCode;
document.querySelector('head meta[name="description"]')?.setAttribute('content', i18n.document.description);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />} errorElement={<PageError />}>
      <Route path={NavigationService.AUTH_CALLBACK} element={<AuthCallbackPage />} />
      <Route path={NavigationService.LOGOUT} element={<PageLogout />} />
      <Route path={NavigationService.ERROR_SESSION_EXPIRED} element={<PageSessionsExpired />} />
      <Route path={NavigationService.ERROR} element={<PageError />} />
      <Route element={<AuthenticatedRoute />}>
        <Route path={NavigationService.ERROR_WRONG_TV_PLATFORM} element={<PageWrongTvPlatform />} />
        <Route path={NavigationService.ERROR_MULTIPLE_TV_OFFERS} element={<PageMultipleTvOffers />} />
        <Route path={NavigationService.ERROR_NO_TV_OFFER} element={<PageNoTvPlatform />} />
        <Route path={NavigationService.ERROR_NO_BROADBAND} element={<PageNoBroadband />} />
        <Route path={NavigationService.ERROR_UNDER_MAINTENANCE} element={<PageUnderMaintenance />} />
        <Route path={NavigationService.ERROR_RESTRICTED_PURCHASE} element={<PageRestrictedPurchase />} />
        <Route path={NavigationService.ERROR_NO_SERVICE_LOCATIONS} element={<PageNoServiceLocations />} />
        <Route path={NavigationService.ERROR_NOT_AVAILABLE} element={<PageNotAvailable />} />
        <Route path={NavigationService.ERROR_HAS_INFLIGHT_ORDER} element={<PageInflightOrder />} />
        <Route path={NavigationService.WRONG_ROLE} element={<PageWrongRole />} />
        <Route path="*" element={<StoreRoutes />} />
      </Route>
    </Route>,
  ),
  {
    basename: process.env.PUBLIC_URL,
  },
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistStore(store)} loading={<Spinner />}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <RouterProvider router={router} />
        </ThemeProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);
